import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Type definitions for wizard data
interface PersonalInfo {
  hasPartner: boolean;
  client1: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
  };
  client2: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
  };
  contact: {
    email: string;
    phone: string;
    advisorFirstName?: string;
    advisorLastName?: string;
  };
}

interface EmploymentInfo {
  client1: {
    isRetired: boolean;
    currentIncome: number;
    expectedRetirementAge: number;
    hasPension?: boolean;
    pensionStartAge?: number;
    pensionBenefitAmount?: number;
  };
  client2: {
    isRetired: boolean;
    currentIncome: number;
    expectedRetirementAge: number;
    hasPension?: boolean;
    pensionStartAge?: number;
    pensionBenefitAmount?: number;
  };
}

interface SocialSecurityInfo {
  isClaiming?: boolean;
  partnerIsClaiming?: boolean;
  client1: {
    currentAmount?: number;
    projectedAmount?: number;
    claimingAge?: number;
  };
  client2: {
    currentAmount?: number;
    projectedAmount?: number;
    claimingAge?: number;
  };
}

interface GoalsInfo {
  targetIncome: number;
  selected: {
    financialIndependence: boolean;
    travel: boolean;
    legacy: boolean;
    healthcare: boolean;
    lifestyle: boolean;
    other: boolean;
  };
  otherGoalDescription: string;
}

interface SavingsInfo {
  taxDeferred: Array<{
    accountName: string;
    balance: number;
    owner: 'client1' | 'client2' | 'joint';
  }>;
  taxFree: Array<{
    accountName: string;
    balance: number;
    owner: 'client1' | 'client2' | 'joint';
  }>;
  taxable: Array<{
    accountName: string;
    balance: number;
    owner: 'client1' | 'client2' | 'joint';
  }>;
}

// Health & Support answers (Limited Interview – HealthSpan). One set per person:
// `primary` always, `partner` collected on a second page when the client has a partner.
export interface HealthSupportAnswers {
  healthyMeals?: string;            // HQ1: 'Daily' | 'Regularly' | 'Unsure' | 'Infrequent' | 'Rarely'
  communityConnection?: string;    // HQ2: 'Very closely connected' | 'Connected' | 'Unsure' | 'Disconnected' | 'Very disconnected'
  healthConditions: string[];      // HQ3
  healthConditionsNone?: boolean;  // HQ3 "None of these"
  relyOnForCare: string[];         // HQ4
  dependOnYou: string[];           // HQ5
  threeAmCall: {                   // HQ6 — name-only in the UI; relationship kept for legacy saved sessions
    relationship?: string;
    name?: string;
  };
}

export interface HealthSupportInfo {
  primary: HealthSupportAnswers;
  partner?: HealthSupportAnswers;  // collected only when the client has a partner
  planningTogether?: boolean;
}

export interface WizardData {
  // Portal advisor id from the URL / advisor config. Legacy advisors use numeric
  // ids; generated advisors use string ids (e.g. "adv-52253dbe"), so both are allowed.
  customerId?: number | string;
  sessionId?: string;
  sessionStartTime?: string;
  lastSaved?: string;
  expiresAt?: string;
  sipsCaseId?: number;
  wizardMode?: 'basic' | 'full';      // Which step flow was used
  prefillSessionId?: string;            // Source session ID for upgrade flow
  personal: PersonalInfo;
  employment: EmploymentInfo;
  socialSecurity: SocialSecurityInfo;
  savings: SavingsInfo;
  goals: GoalsInfo;
  healthSupport?: HealthSupportInfo;  // Limited Interview – HealthSpan only
  totalAssets?: number;  // Used by basic intake: single lump-sum before backend splits by reg type
  completed: boolean;
  uiState: {
    hasAnsweredPartnerQuestion: boolean;
    hasAnsweredRetirementQuestion: boolean;
    hasAnsweredPartnerRetirementQuestion: boolean;
    hasAnsweredSocialSecurityQuestion: boolean;
    isClaimingSocialSecurity?: boolean;
    hasAnsweredPartnerSocialSecurityQuestion: boolean;
    hasAnsweredGoalsQuestion: boolean;
    // Add any other UI state flags here
  };
}

interface WizardContextType {
  currentStep: number;
  setCurrentStep: (step: number) => void;
  wizardData: WizardData;
  updateWizardData: (data: Partial<WizardData>) => void;
  clearWizardData: () => void;
}

const WizardContext = createContext<WizardContextType | undefined>(undefined);

export const initialWizardData: WizardData = {
  customerId: undefined,
  sessionId: undefined,
  sessionStartTime: undefined,
  lastSaved: undefined,
  wizardMode: undefined,
  prefillSessionId: undefined,
  personal: {
    hasPartner: false,
    client1: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
    },
    client2: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
    },
    contact: {
      email: '',
      phone: '',
      advisorFirstName: '',
      advisorLastName: '',
    },
  },
  employment: {
    client1: {
      isRetired: false,
      currentIncome: 0,
      expectedRetirementAge: 0,
      hasPension: undefined,
      pensionStartAge: undefined,
      pensionBenefitAmount: undefined,
    },
    client2: {
      isRetired: false,
      currentIncome: 0,
      expectedRetirementAge: 0,
      hasPension: undefined,
      pensionStartAge: undefined,
      pensionBenefitAmount: undefined,
    },
  },
  socialSecurity: {
    isClaiming: undefined,
    partnerIsClaiming: undefined,
    client1: {
      currentAmount: 0,
      projectedAmount: undefined,
      claimingAge: undefined,
    },
    client2: {
      currentAmount: 0,
      projectedAmount: undefined,
      claimingAge: undefined,
    },
  },
  savings: {
    taxDeferred: [],
    taxFree: [],
    taxable: [],
  },
  goals: {
    targetIncome: 0,
    selected: {
      financialIndependence: false,
      travel: false,
      legacy: false,
      healthcare: false,
      lifestyle: false,
      other: false,
    },
    otherGoalDescription: '',
  },
  healthSupport: {
    primary: {
      healthyMeals: undefined,
      communityConnection: undefined,
      healthConditions: [],
      healthConditionsNone: false,
      relyOnForCare: [],
      dependOnYou: [],
      threeAmCall: { relationship: undefined, name: undefined },
    },
    partner: {
      healthyMeals: undefined,
      communityConnection: undefined,
      healthConditions: [],
      healthConditionsNone: false,
      relyOnForCare: [],
      dependOnYou: [],
      threeAmCall: { relationship: undefined, name: undefined },
    },
    planningTogether: false,
  },
  totalAssets: undefined,
  completed: false,
  uiState: {
    hasAnsweredPartnerQuestion: false,
    hasAnsweredRetirementQuestion: false,
    hasAnsweredPartnerRetirementQuestion: false,
    hasAnsweredSocialSecurityQuestion: false,
    isClaimingSocialSecurity: false,
    hasAnsweredPartnerSocialSecurityQuestion: false,
    hasAnsweredGoalsQuestion: false,
  },
};

// No longer need expiration logic as data is stored server-side

export const WizardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardData, setWizardData] = useState<WizardData>(() => {
    // Start with initial data - will be populated from server if resuming
    return initialWizardData;
  });

  // Memoize the updateWizardData function to prevent it from changing on every render
  const updateWizardData = useCallback((data: Partial<WizardData>) => {
    setWizardData(prevData => {
      const newData = { ...prevData, ...data };
      // Store sessionId in sessionStorage for current browser session
      if (data.sessionId !== undefined) {
        if (data.sessionId) {
          sessionStorage.setItem('wizardSessionId', data.sessionId);
        } else {
          sessionStorage.removeItem('wizardSessionId');
        }
      }
      return newData;
    });
  }, []);

  // Add clearWizardData function
  const clearWizardData = useCallback(() => {
    setWizardData(initialWizardData);
    sessionStorage.removeItem('wizardSessionId');
  }, []);

  // No longer need to save on beforeunload as data is saved to server on each step

  return (
    <WizardContext.Provider value={{ currentStep, setCurrentStep, wizardData, updateWizardData, clearWizardData }}>
      {children}
    </WizardContext.Provider>
  );
};

export const useWizard = () => {
  const context = useContext(WizardContext);
  if (context === undefined) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
}; 